import { CompanyAuth } from "../entities/login/CompanyAuth";
import { FilterRequest } from "../entities/Filter/FilterRequest";

// Built-in fields (negative IDs) backed by a fixed list, mirroring FilterService's
// DataType.List definitions (Segment, Invoice Status).
const BUILT_IN_LIST_FIELD_IDS = ["-7", "-1015"];

// Built-in fields (negative IDs) backed by a Currency/Number value, mirroring
// FilterService's Client Amount/AmountDue (-9/-10) and Invoice Amount/PendingAmount
// (-1011/-1012) definitions.
const BUILT_IN_NUMERIC_FIELD_IDS = ["-9", "-10", "-1011", "-1012"];

function negateOperator(
  fieldId: string,
  additionals: CompanyAuth.AdditionalDefinition[]
): number {
  if (BUILT_IN_NUMERIC_FIELD_IDS.includes(fieldId)) return 5; // Currency/Number → selOperator IsNot
  if (BUILT_IN_LIST_FIELD_IDS.includes(fieldId)) return 1; // List → operatorList IsNot
  const def = additionals.find(
    (a) => String(a.AdditionalDefinitionID) === fieldId
  );
  if (def?.Type === 1) return 1; // List → operatorList IsNot
  return 4; // Text/other → operatorContains DoesntContain
}

/**
 * Parses a dashboard.Filter compact string
 * ("fieldId=value", "fieldId<>value", "fieldId>value", "fieldId<value")
 * into FilterRequest[] entries compatible with AdvancedFilter operators.
 *
 * Operator mapping:
 *   "="  → 0  (selOperator EqualTo, same code for every field kind)
 *   ">"  → 2  (selOperator MoreThan, i.e. "greater than" per numericOperator)
 *   "<"  → 1  (selOperator LessThan, i.e. "less than" per numericOperator)
 *   "<>" → -9/-10/-1011/-1012 (Amount/AmountDue/PendingAmount) → 5 (selOperator IsNot)
 *          -7/-1015 (Segment/IOStatus)                        → 1 (operatorList IsNot)
 *          Positive ID, Type=1                                → 1 (operatorList IsNot)
 *          Other                                              → 4 (operatorContains DoesntContain)
 */
export function parseDashboardFilter(
  filterStr: string,
  additionals: CompanyAuth.AdditionalDefinition[] = []
): FilterRequest[] {
  if (!filterStr) return [];
  return filterStr
    .split("&")
    .filter(Boolean)
    .map((part) => {
      const negIdx = part.indexOf("<>");
      if (negIdx !== -1) {
        const fieldId = part.slice(0, negIdx);
        return {
          field: fieldId,
          operator: negateOperator(fieldId, additionals),
          value: part.slice(negIdx + 2)
        };
      }
      const gtIdx = part.indexOf(">");
      if (gtIdx !== -1) {
        return {
          field: part.slice(0, gtIdx),
          operator: 2,
          value: part.slice(gtIdx + 1)
        };
      }
      const ltIdx = part.indexOf("<");
      if (ltIdx !== -1) {
        return {
          field: part.slice(0, ltIdx),
          operator: 1,
          value: part.slice(ltIdx + 1)
        };
      }
      const eqIdx = part.indexOf("=");
      if (eqIdx === -1) return { field: part, operator: 0, value: "" };
      return {
        field: part.slice(0, eqIdx),
        operator: 0,
        value: part.slice(eqIdx + 1)
      };
    });
}
